import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import dayjs from "dayjs";
import toast from "react-hot-toast";
import axiosInstance from "../../../axiosInstanceSetup";
import {
  STATUS_OPTIONS,
  CATEGORY_OPTIONS,
  mapFrontendStatusToApi,
  mapFrontendCategoryToApi,
  mapFrontendRoadmapStatusToApi,
  mapApiStatusToFrontend,
  transformApiItemToFrontend,
} from "./featureConfig";

const INITIAL_PAGE_SIZE = 80;
const MORE_PAGE_SIZE = 40;

function useDebounce(value, delay = 500) {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debouncedValue;
}

export const useFeatureRequests = () => {
  const currentUserId = useMemo(() => localStorage.getItem("userId"), []);

  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState([]);
  const statusFilterRef = useRef([]);
  const categoryFilterRef = useRef([]);
  const isLoadingRef = useRef(false);

  const [columnSearch, setColumnSearch] = useState({});
  const columnSearchRef = useRef(columnSearch);

  const [activeSearchColumn, setActiveSearchColumn] = useState(null);
  const activeSearchColumnRef = useRef(activeSearchColumn);

  const [sortBy, setSortBy] = useState("");
  const sortByRef = useRef(sortBy);
  const [sortOrder, setSortOrder] = useState("asc");
  const sortOrderRef = useRef(sortOrder);

  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [featureCounts, setFeatureCounts] = useState({});

  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const loadMorePageRef = useRef(Math.ceil(INITIAL_PAGE_SIZE / MORE_PAGE_SIZE) + 1);
  const loadedCountRef = useRef(0);
  const startDateRef = useRef("");
  const endDateRef = useRef("");

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [searchQuery, setSearchQuery] = useState("");

  const [selectedRequest, setSelectedRequest] = useState(null);
  const [isDetailOpen, setDetailOpen] = useState(false);
  const [isSubmitOpen, setSubmitOpen] = useState(false);

  const [loadingDetails, setLoadingDetails] = useState(false);
  const [submittingComment, setSubmittingComment] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [savingAdmin, setSavingAdmin] = useState(false);
  const [upvoting, setUpvoting] = useState(false);

  const [adminForm, setAdminForm] = useState({});
  const [commentText, setCommentText] = useState("");

  const [apiEnums, setApiEnums] = useState({ statuses: [], categories: [] });
  const [enumsLoading, setEnumsLoading] = useState(false);
  const [roadmapLoading, setRoadmapLoading] = useState(false);

  const isSuperuser = useMemo(() => {
    try {
      const role = JSON.parse(localStorage.getItem("role") || "");
      return role;
    } catch {
      return false;
    }
  }, []);

  useEffect(() => {
    statusFilterRef.current = statusFilter;
  }, [statusFilter]);
  useEffect(() => {
    categoryFilterRef.current = categoryFilter;
  }, [categoryFilter]);
  useEffect(() => {
    columnSearchRef.current = columnSearch;
  }, [columnSearch]);
  useEffect(() => {
    activeSearchColumnRef.current = activeSearchColumn;
  }, [activeSearchColumn]);
  useEffect(() => {
    sortByRef.current = sortBy;
  }, [sortBy]);
  useEffect(() => {
    sortOrderRef.current = sortOrder;
  }, [sortOrder]);
  useEffect(() => {
    startDateRef.current = startDate;
  }, [startDate]);
  useEffect(() => {
    endDateRef.current = endDate;
  }, [endDate]);

  const loadEnums = useCallback(async () => {
    setEnumsLoading(true);
    try {
      const res = await axiosInstance.get(
        `${process.env.REACT_APP_API_URL}/feature-enums`,
      );
      const data = res.data || {};
      setApiEnums({
        statuses: Array.isArray(data.statuses) ? data.statuses : [],
        categories: Array.isArray(data.categories) ? data.categories : [],
      });
    } catch (err) {
      console.warn(
        "Failed to load feature-enums, falling back to defaults",
        err,
      );
      setApiEnums({
        statuses: STATUS_OPTIONS.map((s) => s.label).filter(Boolean),
        categories: CATEGORY_OPTIONS.map((c) => c.label).filter(Boolean),
      });
    } finally {
      setEnumsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadEnums();
  }, [loadEnums]);

  const loadRoadmap = useCallback(async (params = {}) => {
    setRoadmapLoading(true);
    try {
      const qs = new URLSearchParams();
      if (params.start_date) qs.append("startDate", params.start_date);
      if (params.end_date) qs.append("endDate", params.end_date);

      const currentCategoryFilter = params.category;
      const currentStatusFilter = params.status;

      if (
        Array.isArray(currentCategoryFilter) &&
        currentCategoryFilter.length > 0
      ) {
        const apiCategories = currentCategoryFilter
          .map((category) => mapFrontendCategoryToApi(category))
          .filter(Boolean);
        if (apiCategories.length > 0) {
          qs.append("category", apiCategories.join(","));
        }
      }

      if (
        Array.isArray(currentStatusFilter) &&
        currentStatusFilter.length > 0
      ) {
        const apiStatuses = currentStatusFilter
          .map((status) => mapFrontendRoadmapStatusToApi(status))
          .filter(Boolean);
        if (apiStatuses.length > 0) {
          qs.append("status", apiStatuses.join(","));
        }
      }

      if (params.page) qs.append("page", String(params.page));
      if (params.page_size) qs.append("page_size", String(params.page_size));
      if (params.sort_by) qs.append("sort_by", String(params.sort_by));
      if (params.sort_order) qs.append("sort_order", String(params.sort_order));
      if (params.search_by) qs.append("search_by", String(params.search_by));
      if (params.search_value)
        qs.append("search_value", String(params.search_value));

      const url = `${process.env.REACT_APP_API_URL}/roadmap?${qs.toString()}`;
      const res = await axiosInstance.get(url);
      return res.data;
    } catch (err) {
      console.error("Failed to load roadmap:", err);
      toast.error(
        err?.response?.data?.detail ||
        "Failed to load roadmap. Please try again.",
      );
      return null;
    } finally {
      setRoadmapLoading(false);
    }
  }, []);

  const canCurrentUserDelete = useCallback(
    (request) => {
      if (!request) return false;
      if (isSuperuser) return true;

      const userId = window.localStorage.getItem("userId");
      const userEmail = window.localStorage.getItem("userEmail");
      const userName = window.localStorage.getItem("userName");

      const emailMatch =
        request.submittedByEmail &&
        userEmail &&
        request.submittedByEmail.toLowerCase() === userEmail.toLowerCase();
      const idMatch =
        request.submittedById && userId && request.submittedById === userId;
      const nameMatch =
        !request.submittedByEmail &&
        request.submittedBy &&
        userName &&
        request.submittedBy.toLowerCase() === userName.toLowerCase();
      return Boolean(emailMatch || idMatch || nameMatch);
    },
    [isSuperuser],
  );

  const buildRequestParams = useCallback((params = {}, pageNum = 1, pageSize = INITIAL_PAGE_SIZE) => {
    const requestParams = new URLSearchParams({
      page: pageNum.toString(),
      page_size: pageSize.toString(),
    });

    if (currentUserId) requestParams.append("user_id", currentUserId);

    const computeDefaultRange = () => {
      const today = new Date();
      const s = new Date(today);
      s.setDate(s.getDate() - 30);
      const e = new Date(today);
      e.setDate(e.getDate() + 90);
      return { start: s.toISOString().split("T")[0], end: e.toISOString().split("T")[0] };
    };
    const defaults = computeDefaultRange();
    const effectiveStart = params.start_date || startDateRef.current || defaults.start;
    const effectiveEnd = params.end_date || endDateRef.current || defaults.end;
    if (effectiveStart) requestParams.append("startDate", effectiveStart);
    if (effectiveEnd) requestParams.append("endDate", effectiveEnd);

    const currentStatusFilter = params.status || statusFilterRef.current;
    const currentCategoryFilter = params.category || categoryFilterRef.current;

    if (Array.isArray(currentStatusFilter) && currentStatusFilter.length > 0) {
      const apiStatuses = currentStatusFilter.map(mapFrontendStatusToApi).filter(Boolean);
      if (apiStatuses.length > 0) requestParams.append("status", apiStatuses.join(","));
    }
    if (Array.isArray(currentCategoryFilter) && currentCategoryFilter.length > 0) {
      const apiCategories = currentCategoryFilter.map(mapFrontendCategoryToApi).filter(Boolean);
      if (apiCategories.length > 0) requestParams.append("category", apiCategories.join(","));
    }

    const currentColumnSearch = columnSearchRef.current;
    const currentActiveSearchColumn = activeSearchColumnRef.current;
    if (currentActiveSearchColumn && currentColumnSearch[currentActiveSearchColumn]?.trim()) {
      requestParams.append("search_by", currentActiveSearchColumn);
      requestParams.append("search_value", currentColumnSearch[currentActiveSearchColumn].trim());
    }

    const currentSortBy = params.sort_by || sortByRef.current;
    const currentSortOrder = params.sort_order || sortOrderRef.current;
    if (currentSortBy) {
      requestParams.append("sort_by", currentSortBy);
      requestParams.append("sort_order", currentSortOrder);
    }

    return requestParams;
  }, [currentUserId]);

  const loadRequests = useCallback(async (params = {}) => {
    if (isLoadingRef.current) return;
    isLoadingRef.current = true;
    setLoading(true);
    try {
      const requestParams = buildRequestParams(params, 1, INITIAL_PAGE_SIZE);
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_API_URL}/features?${requestParams.toString()}`,
      );

      if (!response.data?.items) throw new Error("Invalid response format");

      const transformedItems = response.data.items.map(transformApiItemToFrontend);
      setRequests(transformedItems);
      setTotal(response.data.total);
      setTotalPages(response.data.total_pages);
      setFeatureCounts(response.data.featureCounts || {});
      loadedCountRef.current = transformedItems.length;
      setHasMore(response.data.total > transformedItems.length);
      loadMorePageRef.current = Math.ceil(INITIAL_PAGE_SIZE / MORE_PAGE_SIZE) + 1;
    } catch (error) {
      console.error("Failed to load features from API:", error);
      toast.error(
        error?.response?.data?.detail ||
        "Failed to load feature requests. Please try again.",
      );
      setRequests([]);
      setTotal(0);
      setTotalPages(1);
      setHasMore(false);
    } finally {
      isLoadingRef.current = false;
      setLoading(false);
    }
  }, [buildRequestParams]);

  const loadMoreRequests = useCallback(async () => {
    if (isFetchingMore || !hasMore || isLoadingRef.current) return;
    setIsFetchingMore(true);
    try {
      const requestParams = buildRequestParams({}, loadMorePageRef.current, MORE_PAGE_SIZE);
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_API_URL}/features?${requestParams.toString()}`,
      );

      if (!response.data?.items) return;

      const newItems = response.data.items.map(transformApiItemToFrontend);
      setRequests((prev) => [...prev, ...newItems]);
      setTotal(response.data.total);
      loadedCountRef.current += newItems.length;
      setHasMore(loadedCountRef.current < response.data.total);
      loadMorePageRef.current += 1;
    } catch (error) {
      console.error("Failed to load more features:", error);
      toast.error("Failed to load more features. Please try again.");
    } finally {
      setIsFetchingMore(false);
    }
  }, [isFetchingMore, hasMore, buildRequestParams]);



  const filteredRequests = useMemo(() => {
    const lowerSearch = searchQuery.toLowerCase();
    const filtered = requests.filter((request) =>
      request.title.toLowerCase().includes(lowerSearch),
    );
    return filtered;
  }, [requests, searchQuery]);

  const updateRequestState = useCallback((requestId, updater) => {
    setRequests((prev) =>
      prev.map((item) => (item.id === requestId ? updater(item) : item)),
    );
    setSelectedRequest((prev) => {
      if (!prev || prev.id !== requestId) return prev;
      return updater(prev);
    });
  }, []);

  const transformDetailResponse = useCallback((apiResponse) => {
    const base = transformApiItemToFrontend(apiResponse);

    const comments = (apiResponse.comments || []).map((comment) => ({
      id: comment.id || `c-${Date.now()}`,
      author: comment.created_by || "Unknown",
      message: comment.comment_text || "",
      createdAt: comment.created_date || new Date().toISOString(),
      isAdmin: comment.is_admin || false,
      userId: comment.user_id || "",
    }));

    const onboard_product = apiResponse?.onboard_product;
    const productDetails =
      onboard_product &&
        (onboard_product.product_code || onboard_product.product_name)
        ? {
          productCode: onboard_product.product_code || "",
          productName: onboard_product.product_name || "",
          productOwner: onboard_product.product_owner || "",
          productDescription: onboard_product.product_description || "",
          domain: onboard_product.domain || "",
          subDomain: onboard_product.subdomain || "",
          parentDomain: onboard_product.parent_domain || "",
          ucSchema: onboard_product.uc_schema || "",
          adlsPath: onboard_product.adls_path || "",
          ucGcpSchema: onboard_product.uc_schema_gcp || "",
        }
        : undefined;

    return {
      ...base,
      comments,
      productDetails,
      projectName: apiResponse.project_name || base.projectName || "",
      priority: base.priority,
      eta: apiResponse.eta || base.eta || "",
      assignedTo: apiResponse.assigned_to || "",
      modifiedDate: apiResponse.modified_date || "",
      modifiedBy: apiResponse.modified_by || "",
    };
  }, []);

  const openRequestDetails = useCallback(
    async (request) => {
      const latest = requests.find((item) => item.id === request.id) || request;
      setDetailOpen(true);
      setSelectedRequest(latest);
      setCommentText("");
      setAdminForm({
        status: latest.status || "PENDING_REVIEW",
        priority: latest.priority || "low",
        eta: latest.eta,
        submittedBy: latest.submittedBy || "",
      });

      setLoadingDetails(true);
      try {
        const response = await axiosInstance.get(
          `${process.env.REACT_APP_API_URL}/feature/${request.id}`,
        );

        if (response.data) {
          const detailedRequest = transformDetailResponse(response.data);
          setSelectedRequest(detailedRequest);
          setAdminForm({
            status: detailedRequest.status || "PENDING_REVIEW",
            priority: detailedRequest.priority,
            eta: detailedRequest.eta,
            assignedTo: detailedRequest.assignedTo || "",
            submittedBy: detailedRequest.submittedBy || "",
            adlsPath: detailedRequest?.productDetails?.adlsPath,
            ucGcpSchema: detailedRequest?.productDetails?.ucGcpSchema,
            ucSchema: detailedRequest?.productDetails?.ucSchema,
          });

          updateRequestState(request.id, () => detailedRequest);
        }
      } catch (error) {
        console.error("Failed to load feature details from API:", error);
        toast.error("Failed to load full details. Showing summary.");
      } finally {
        setLoadingDetails(false);
      }
    },
    [requests, transformDetailResponse, updateRequestState],
  );

  const viewRequestDetails = useCallback(
    async (featureId) => {
      setDetailOpen(true);
      setSelectedRequest({});
      setCommentText("");
      setAdminForm({ status: "PENDING_REVIEW", priority: "low", eta: "", submittedBy: "" });

      setLoadingDetails(true);
      try {
        const response = await axiosInstance.get(
          `${process.env.REACT_APP_API_URL}/feature/${featureId}`,
        );

        if (response.data) {
          const detailedRequest = transformDetailResponse(response.data);
          setSelectedRequest(detailedRequest);
          setAdminForm({
            status: detailedRequest.status || "PENDING_REVIEW",
            priority: detailedRequest.priority,
            eta: detailedRequest.eta,
            submittedBy: detailedRequest.submittedBy || "",
          });

          updateRequestState(featureId, () => detailedRequest);
        }
      } catch (error) {
        console.error("Failed to load feature details from API:", error);
        toast.error("Failed to load full details. Showing summary.");
      } finally {
        setLoadingDetails(false);
      }
    },
    [requests, transformDetailResponse, updateRequestState],
  );

  const handleToggleUpvote = useCallback(
    async (request) => {
      if (!request || upvoting) return;
      const userId = window.localStorage.getItem("userId");
      if (!userId) {
        toast.error("You need to be logged in to upvote.");
        return;
      }
      const createdBy = window.localStorage.getItem("userName");
      const nextHasVoted = !request.hasVoted;
      const delta = nextHasVoted ? 1 : -1;

      setUpvoting(true);

      updateRequestState(request.id, (item) => ({
        ...item,
        hasVoted: nextHasVoted,
        upvotes: Math.max(0, item.upvotes + delta),
      }));

      try {
        const response = await axiosInstance.post(
          `${process.env.REACT_APP_API_URL}/feature/${request.id}/upvote`,
          { user_id: userId, created_by: createdBy },
        );

        const action = response?.data?.action;
        const upvotesFromServer = response?.data?.upvotes_count;
        const finalHasVoted =
          action === "added"
            ? true
            : action === "removed"
              ? false
              : nextHasVoted;

        updateRequestState(request.id, (item) => ({
          ...item,
          hasVoted: finalHasVoted,
          upvotes:
            typeof upvotesFromServer === "number"
              ? upvotesFromServer
              : item.upvotes,
        }));

        await loadRequests({ start_date: startDate, end_date: endDate });
      } catch (error) {
        updateRequestState(request.id, (item) => ({
          ...item,
          hasVoted: !nextHasVoted,
          upvotes: Math.max(0, item.upvotes - delta),
        }));
        toast.error(
          error?.response?.data?.detail ||
          "Failed to update upvote. Please try again.",
        );
      } finally {
        setUpvoting(false);
      }
    },
    [updateRequestState, loadRequests, upvoting],
  );

  const handleSubmitComment = useCallback(async () => {
    if (!commentText.trim() || !selectedRequest || submittingComment) return;

    const payload = {
      comment_text: commentText.trim(),
      user_id: window.localStorage.getItem("userId") || "",
      created_by: window.localStorage.getItem("userName") || "Anonymous User",
    };

    setSubmittingComment(true);

    try {
      const response = await axiosInstance.post(
        `${process.env.REACT_APP_API_URL}/feature/${selectedRequest.id}/comment`,
        payload,
      );

      setLoadingDetails(true);
      try {
        const detailResponse = await axiosInstance.get(
          `${process.env.REACT_APP_API_URL}/feature/${selectedRequest.id}`,
        );

        if (detailResponse.data) {
          const detailedRequest = transformDetailResponse(detailResponse.data);
          setSelectedRequest(detailedRequest);

          updateRequestState(selectedRequest.id, () => detailedRequest);
        }
      } catch (error) {
        console.error("Failed to refresh feature details:", error);
        const newComment = {
          id: response?.data?.comment_id || `c-${Date.now()}`,
          author: response?.data?.created_by || payload.created_by,
          message: payload.comment_text,
          createdAt: response?.data?.created_date || new Date().toISOString(),
        };

        updateRequestState(selectedRequest.id, (item) => ({
          ...item,
          comments: [...(item.comments || []), newComment],
        }));
      } finally {
        setLoadingDetails(false);
      }

      setCommentText("");
      toast.success("Comment added successfully.");
    } catch (error) {
      toast.error(
        error?.response?.data?.detail ||
        "Failed to add comment. Please try again.",
      );
    } finally {
      setSubmittingComment(false);
    }
  }, [
    commentText,
    selectedRequest,
    updateRequestState,
    submittingComment,
    transformDetailResponse,
  ]);

  const handleAdminSave = useCallback(async () => {
    if (!selectedRequest) return;

    const userId = window.localStorage.getItem("userId");
    const updatedBy = window.localStorage.getItem("userName") || "Admin User";

    if (!userId) {
      toast.error("User ID not found. Please log in again.");
      return;
    }

    const apiStatus = mapFrontendStatusToApi(adminForm.status);
    if (!apiStatus) {
      toast.error("Invalid status value.");
      return;
    }

    setSavingAdmin(true);

    try {
      const payload = {
        status: apiStatus,
        user_id: userId,
        updated_by: updatedBy,
        priority: adminForm.priority,
        eta: adminForm.eta,
        assigned_to: adminForm.assignedTo,
        created_by: adminForm.submittedBy,
      };

      if (selectedRequest.category === "REGISTER_PRODUCT") {
        const originalProductDetails = selectedRequest.productDetails || {};
        const productData = {};

        if (
          adminForm.productName &&
          adminForm.productName !== originalProductDetails.productName
        ) {
          productData.product_name = adminForm.productName;
        }
        if (
          adminForm.ucSchema &&
          adminForm.ucSchema !== originalProductDetails.ucSchema
        ) {
          productData.uc_schema = adminForm.ucSchema;
        }
        if (
          adminForm.ucGcpSchema &&
          adminForm.ucGcpSchema !== originalProductDetails.ucGcpSchema
        ) {
          productData.uc_schema_gcp = adminForm.ucGcpSchema;
        }
        if (
          adminForm.adlsPath &&
          adminForm.adlsPath !== originalProductDetails.adlsPath
        ) {
          productData.adls_path = adminForm.adlsPath;
        }
        if (
          adminForm.parentDomain &&
          adminForm.parentDomain !== originalProductDetails.parentDomain
        ) {
          productData.parent_domain = adminForm.parentDomain;
        }
        if (
          adminForm.domain &&
          adminForm.domain !== originalProductDetails.domain
        ) {
          productData.domain = adminForm.domain;
        }
        if (
          adminForm.subDomain &&
          adminForm.subDomain !== originalProductDetails.subDomain
        ) {
          productData.subdomain = adminForm.subDomain;
        }

        if (Object.keys(productData).length > 0) {
          payload.product_data = productData;
        }
      }

      const response = await axiosInstance.patch(
        `${process.env.REACT_APP_API_URL}/feature/${selectedRequest.id}/status`,
        payload,
      );

      if (response.data) {
        const updatedStatus = mapApiStatusToFrontend(response.data.status);
        updateRequestState(selectedRequest.id, (item) => ({
          ...item,
          status: updatedStatus,
          priority: response.data.priority,
          eta: response.data.eta,
          submittedBy: response.data.created_by || item.submittedBy,
          modifiedDate: response.data.modified_date || item.modifiedDate,
          modifiedBy: response.data.modified_by || item.modifiedBy,
          productDetails:
            selectedRequest.category === "REGISTER_PRODUCT"
              ? {
                ...item.productDetails,
                productName:
                  adminForm.productName || item.productDetails?.productName,
                ucSchema: adminForm.ucSchema || item.productDetails?.ucSchema,
                parentDomain:
                  adminForm.parentDomain || item.productDetails?.parentDomain,
                domain: adminForm.domain || item.productDetails?.domain,
                subDomain:
                  adminForm.subDomain || item.productDetails?.subDomain,
              }
              : item.productDetails,
        }));

        setAdminForm((prev) => ({
          ...prev,
          status: updatedStatus,
          priority: response.data.priority,
          eta: response.data.eta,
          assignedTo: response.data.assigned_to || "",
          submittedBy: response.data.created_by || prev.submittedBy,
        }));

        toast.success("Changes saved successfully.");
      }
    } catch (error) {
      console.error("Failed to update feature status:", error);

      let errorMessage = "Failed to update status. Please try again.";
      if (error.response) {
        switch (error.response.status) {
          case 403:
            errorMessage = "You don't have permission to update the status.";
            break;
          case 400:
            errorMessage =
              error.response.data?.detail || "Invalid status value.";
            break;
          case 404:
            errorMessage = "Feature not found.";
            break;
          case 500:
            errorMessage = "Server error. Please try again later.";
            break;
          default:
            errorMessage = error.response.data?.detail || errorMessage;
        }
      }

      toast.error(errorMessage);
    } finally {
      setSavingAdmin(false);
    }
  }, [selectedRequest, adminForm, updateRequestState, setAdminForm]);

  const handleDeleteRequest = useCallback(() => {
    if (!selectedRequest || !canCurrentUserDelete(selectedRequest)) return;
    setRequests((prev) =>
      prev.filter((item) => item.id !== selectedRequest.id),
    );
    setDetailOpen(false);
    setSelectedRequest(null);
  }, [selectedRequest, canCurrentUserDelete]);

  const handleColumnSearchChange = useCallback((columnKey, value) => {
    setColumnSearch({
      title: "",
      status: "",
      category: "",
      priority: "",
      submitted_by: "",
      submitted_date: "",
      assigned_to: "",
      [columnKey]: value,
    });
    setActiveSearchColumn(value.trim() ? columnKey : "");
    setSortBy("");
    setSortOrder("asc");
    setPage(1);
  }, []);

  const handleSortChange = useCallback((columnKey, order) => {
    setSortBy(columnKey);
    setSortOrder(order);
    setPage(1);
  }, []);



  const searchValue = useMemo(() => {
    if (!activeSearchColumn) return "";
    return (columnSearch[activeSearchColumn] || "").trim();
  }, [activeSearchColumn, columnSearch]);

  const debouncedSearch = useDebounce(searchValue, 500);



  useEffect(() => {
    loadRequests();
  }, [loadRequests, sortBy, sortOrder, debouncedSearch]);

  const handleSubmitRequest = useCallback(
    async (formData) => {
      setSubmitting(true);
      const isOnboard = formData.category === "REGISTER_PRODUCT";
      const normalizedTitle = isOnboard
        ? formData.title ||
        formData.productName ||
        `Onboard Product - ${formData.productCode || "New Product"}`
        : formData.title;
      const normalizedDescription = isOnboard
        ? formData.description ||
        formData.productDescription ||
        `Product onboarding request for ${formData.productName || formData.productCode
        }`
        : formData.description;
      const productDetails = isOnboard
        ? {
          productCode: formData.productCode,
          productName: formData.productName,
          productOwner: formData.productOwner,
          productDescription: formData.productDescription,
          domain: formData.domain,
          subDomain: formData.subDomain,
          parentDomain: formData.parentDomain,
          ucSchema: formData.ucSchema,
          adlsPath: formData.adlsPath,
          projectName: formData.projectName,
        }
        : undefined;

      const categoryLabel =
        CATEGORY_OPTIONS.find((option) => option.value === formData.category)
          ?.label || formData.category;

      const payload = {
        title: normalizedTitle,
        category: categoryLabel,
        description: normalizedDescription,
        created_by: window.localStorage.getItem("userName"),
        user_id: window.localStorage.getItem("userId"),
      };

      if (formData.priority) {
        payload.priority = formData.priority.toLowerCase();
      }

      if (formData.eta) {
        payload.eta = formData.eta;
      }
      
      if (isOnboard) {
        const onboardProductPayload = {
          project_name: formData.projectName?.trim() || "",
          product_code: formData.productCode,
          product_name: formData.productName,
          product_owner: formData.productOwner,
          product_description: formData.productDescription,
          domain: formData.domain,
          subdomain: formData.subDomain,
          parent_domain: formData.parentDomain,
        };

        const trimmedUcSchema = (formData.ucSchema || "").trim();
        if (trimmedUcSchema) {
          onboardProductPayload.uc_schema = trimmedUcSchema;
        }
        const trimmedUcGCPSchema = (formData.uc_schema_gcp || "").trim();
        if (trimmedUcGCPSchema) {
          onboardProductPayload.uc_schema_gcp = trimmedUcGCPSchema;
        }

        const trimmedAdlsPath = (formData.adlsPath || "").trim();
        if (trimmedAdlsPath) {
          onboardProductPayload.adls_path = trimmedAdlsPath;
        }

        payload.onboard_product = onboardProductPayload;
      } else if (formData.projectName && formData.projectName.trim()) {
        payload.project_name = formData.projectName.trim();
      }

      try {
        const response = await axiosInstance.post(
          `${process.env.REACT_APP_API_URL}/submit-feature`,
          payload,
        );
        const featureId =
          response?.data?.feature_id ||
          response?.data?.id ||
          `req-${Date.now()}`;

        const newRequest = {
          id: featureId,
          ...formData,
          title: normalizedTitle,
          description: normalizedDescription,
          projectName: formData.projectName || "",
          priority: formData.priority || "",
          eta: formData.eta || "",
          productDetails,
          status: "PENDING_REVIEW",
          upvotes: 0,
          hasVoted: false,
          submittedBy: window.localStorage.getItem("userName") || "You",
          submittedByEmail: window.localStorage.getItem("userEmail") || "",
          submittedById: currentUserId || "",
          createdAt: new Date().toISOString(),
          comments: [],
        };

        setRequests((prev) => [newRequest, ...prev]);
        toast.success("Feature request submitted successfully.");
        setSubmitOpen(false);
        loadRequests({ start_date: startDate, end_date: endDate });
      } catch (error) {
        toast.error(
          error?.response?.data?.message ||
          "Failed to submit feature request. Please try again.",
        );
      } finally {
        setSubmitting(false);
      }
    },
    [currentUserId, loadRequests, startDate, endDate],
  );

  return {
    STATUS_OPTIONS,
    CATEGORY_OPTIONS,
    requests,
    filteredRequests,
    loading,
    statusFilter,
    setStatusFilter,
    categoryFilter,
    setCategoryFilter,
    selectedRequest,
    isDetailOpen,
    setDetailOpen,
    isSubmitOpen,
    setSubmitOpen,
    page,
    setPage,
    totalPages,
    total,
    searchQuery,
    setSearchQuery,
    commentText,
    setCommentText,
    adminForm,
    setAdminForm,
    canCurrentUserDelete,
    openRequestDetails,
    handleToggleUpvote,
    viewRequestDetails,
    handleSubmitComment,
    handleAdminSave,
    handleDeleteRequest,
    handleSubmitRequest,
    submitting,
    savingAdmin,
    loadRequests,
    isSuperuser,
    loadingDetails,
    submittingComment,
    upvoting,
    columnSearch,
    sortBy,
    sortOrder,
    handleColumnSearchChange,
    handleSortChange,
    apiEnums,
    enumsLoading,
    loadRoadmap,
    roadmapLoading,
    activeSearchColumn,
    featureCounts,
    // Date range controlled by page
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    // Infinite scroll
    loadMoreRequests,
    isFetchingMore,
    hasMore,
  };
};
